import React, { useEffect, useState, useRef, Fragment } from "react";
import PropTypes from "kf-proptypes/widgets";

import { useWindowSize } from "base/hooks";

import {
  KfIcon,
  ICON_SIZE,
  ICON_STROKE
} from "@widgets/common/components/primitives/kf_icon/index.jsx";
import { Tooltip } from "../../feedback/tooltip/index.jsx";

import styles from "./styles.css";

PropertyTabs.propTypes = {
  children: PropTypes.node,
  /** Current active tabId */
  activeTabId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  /** Count of tabs to be shrinked when number of tab exceeds 4 */
  shrinkCount: PropTypes.number
};

export function PropertyTabs(props) {
  const { children, activeTabId, shrinkCount = 2 } = props;
  const _children = React.Children.toArray(children);

  const rootRef = useRef(null);
  const highlightRef = useRef(null);

  const [shrinkedIndexArr, setShrinkedIndexArr] = useState([]);
  const { width } = useWindowSize();

  useEffect(
    function updateShrinkWidthIds() {
      if (_children.length > 4) {
        const activeIndex = children.findIndex(
          (child) => child.props?.tabId === activeTabId
        );
        if (
          !shrinkedIndexArr.length ||
          shrinkedIndexArr.includes(activeIndex)
        ) {
          const count = children.length;
          const possibleStartShrinkIndex = Array.from(
            { length: shrinkCount },
            (_, i) => i
          );
          const possibleEndShrinkIndex = Array.from(
            { length: shrinkCount },
            (_, i) => count - shrinkCount + (i % shrinkCount)
          );
          const isInFirstIndex = possibleStartShrinkIndex.indexOf(activeIndex);
          const isInEndIndex = possibleEndShrinkIndex.indexOf(activeIndex);
          let newShrinkIndex = [];
          if (isInFirstIndex > -1) {
            newShrinkIndex.push(
              ...possibleStartShrinkIndex.slice(0, isInFirstIndex)
            );
            if (newShrinkIndex.length < shrinkCount) {
              newShrinkIndex.push(
                ...possibleEndShrinkIndex.slice(newShrinkIndex.length)
              );
            }
          } else if (isInEndIndex > -1) {
            newShrinkIndex.push(
              ...possibleEndShrinkIndex.slice(isInEndIndex + 1)
            );
            if (newShrinkIndex.length < shrinkCount) {
              newShrinkIndex.push(
                ...possibleStartShrinkIndex.slice(
                  0,
                  shrinkCount - newShrinkIndex.length
                )
              );
            }
          } else {
            newShrinkIndex = possibleEndShrinkIndex;
          }
          setShrinkedIndexArr(newShrinkIndex);
        }
      } else {
        setShrinkedIndexArr([]);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [activeTabId, _children.length]
  );

  useEffect(
    function updateHighlight() {
      if (activeTabId) {
        requestAnimationFrame(() => {
          highlightTab(100);
        });
      }
    },
    [activeTabId, _children.length, width]
  );

  useEffect(
    function updateHighlightShrink() {
      requestAnimationFrame(() => {
        highlightTab(300);
      });
    },
    [shrinkedIndexArr]
  );

  function highlightTab(timeout) {
    setTimeout(() => {
      const activeTabEle =
        rootRef.current &&
        rootRef.current.querySelector(`[data-active="true"]`);
      if (activeTabEle) {
        highlightRef.current.style.display = "block";
        highlightRef.current.style.width = `${Math.floor(
          activeTabEle.offsetWidth
        )}px`;
        if (window.isRTL) {
          highlightRef.current.style.left = "auto";
          highlightRef.current.style.right = `${Math.floor(
            activeTabEle.parentElement.offsetWidth -
              activeTabEle.offsetLeft -
              activeTabEle.offsetWidth
          )}px`;
        } else {
          highlightRef.current.style.right = "auto";
          highlightRef.current.style.left = `${Math.floor(
            activeTabEle.offsetLeft
          )}px`;
        }
      } else if (highlightRef.current) {
        highlightRef.current.style.display = "none";
      }
    }, timeout);
  }

  return (
    <div className={styles.propertyTabs} ref={rootRef}>
      <div className={styles.highlight} ref={highlightRef} />
      {_children.map((child, index) => {
        const {
          icon,
          label = "",
          tabId = "",
          tooltipContent = "",
          tooltipPosition,
          onClick,
          iconProps = {},
          isError = false,
          className
        } = child.props;
        const isActive = activeTabId === tabId;
        const Wrapper = tooltipContent ? Tooltip : Fragment;
        return (
          <Wrapper
            key={tabId}
            {...(Wrapper === Tooltip
              ? { content: tooltipContent, position: tooltipPosition }
              : {})}
          >
            <div
              className={`${className} ${styles.singleTab} 
                ${isActive ? styles.activeTab : ""} 
                ${isError ? styles.error : ""}
              `}
              onClick={() => onClick(tabId)}
              data-active={isActive}
              data-shrink={shrinkedIndexArr.includes(index)}
            >
              {icon ? (
                <KfIcon
                  icon={icon}
                  size={ICON_SIZE.SIZE_16}
                  stroke={isActive ? ICON_STROKE.BOLD : ICON_STROKE.THIN}
                  color={
                    isActive ? "--kf-color-primary-500" : "--kf-color-gray-600"
                  }
                  {...iconProps}
                />
              ) : null}
              {label ? (
                <span
                  className={`${styles.tabName} ${
                    isActive ? "micro600" : "micro"
                  }`}
                  dir="auto"
                >
                  {label}
                </span>
              ) : null}
            </div>
          </Wrapper>
        );
      })}
    </div>
  );
}

PropertyTabs.Tab = PropertyTab;

function PropertyTab(props) {
  const { children } = props;
  return children;
}

PropertyTab.propTypes = {
  /** tab icon name from KF_ICON */
  icon: PropTypes.string,
  /** tab icon props from KfIcon props */
  iconProps: PropTypes.objectOf(PropTypes.string),
  /** tab label */
  label: PropTypes.string,
  /** tab unique id */
  tabId: PropTypes.string,
  /** tab's tooltip content */
  tooltipContent: PropTypes.string,
  /** tab's tooltip position */
  tooltipPosition: PropTypes.string,
  /** tab onClick callback */
  onClick: PropTypes.func,
  /** tab className for style override */
  className: PropTypes.string
};
